import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card, SectionHeader } from '@/components/Card';
import { Modal } from '@/components/Modal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { StatTile } from '@/components/StatTile';
import { CampaignStatusPill } from '@/components/facebook/CampaignStatusPill';
import { InsightsTrendStrip } from '@/components/facebook/InsightsTrendStrip';
import { facebookCampaignsService, FbCampaign, getInsights } from '@/services/facebook-campaigns.service';
import { facebookAdAccountService, MetaAdAccount } from '@/services/facebook-ad-account.service';
import { facebookAdSetsService, FbAdSet } from '@/services/facebook-ad-sets.service';
import { facebookAdsService, FbAd } from '@/services/facebook-ads.service';
import { facebookAdCreativesService } from '@/services/facebook-ad-creatives.service';
import { AdsetSection } from '@/components/facebook/campaigns-list';
import type { AdWithCreative } from '@/components/facebook/campaigns-list';
import { leadsAnalyticsService, FunnelResult } from '@/services/leads-analytics.service';
import { formatMetaInsightsValue, formatMetaCount, formatMetaPercent, formatMetaCurrency } from '@/utils/currency';
import { RefreshCw, ExternalLink, Plus, BarChart2, Layers, Settings2, Zap, AlertCircle, Inbox, ArrowUpRight, Trophy } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

// ─── Leads attribution card ───────────────────────────────────────────────────

function LeadsAttributionCard({ campaignId }: { campaignId: string }) {
  const nav = useNavigate();
  const [funnel, setFunnel] = useState<FunnelResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    leadsAnalyticsService
      .getFunnel({ campaignId })
      .then(setFunnel)
      .catch(() => setFunnel(null))
      .finally(() => setLoading(false));
  }, [campaignId]);

  return (
    <Card padded>
      <SectionHeader
        title="Leads from this campaign"
        icon={<Inbox size={15} />}
        action={
          <div className="flex items-center gap-2">
            <button
              onClick={() => nav(`/leads?sourceCampaignId=${campaignId}`)}
              className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
            >
              View leads <ArrowUpRight size={12} />
            </button>
            <button
              onClick={() => nav(`/leads/analytics?campaign=${campaignId}`)}
              className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
            >
              Ad breakdown <ArrowUpRight size={12} />
            </button>
          </div>
        }
      />

      {loading ? (
        <div className="grid grid-cols-3 gap-3 mt-3">
          {[...Array(6)].map((_, i) => <div key={i} className="skeleton h-14 rounded-xl" />)}
        </div>
      ) : !funnel || funnel.total === 0 ? (
        <div className="mt-3 flex flex-col items-center gap-2 py-6 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-surface-alt text-text-muted">
            <Inbox size={18} />
          </div>
          <p className="text-xs text-text-muted">No leads linked to this campaign yet.</p>
          <p className="text-[11px] text-text-muted max-w-xs">
            Leads are created when someone taps a Click-to-WhatsApp ad or submits a Lead Ad form linked to this campaign.
          </p>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
            {[
              { label: 'Total', value: funnel.total, color: 'text-text-primary' },
              { label: 'New', value: funnel.new, color: 'text-info' },
              { label: 'Contacted', value: funnel.contacted, color: 'text-warning' },
              { label: 'Qualified', value: funnel.qualified, color: 'text-primary' },
              { label: 'Won', value: funnel.won, color: 'text-success' },
              { label: 'Lost', value: funnel.lost, color: 'text-danger' },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded-xl border border-border bg-surface-alt p-3 text-center">
                <div className="text-2xs font-semibold uppercase tracking-[0.12em] text-text-muted">{label}</div>
                <div className={clsx('mt-1.5 font-display text-2xl font-bold tabular-nums', color)}>{value}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between rounded-xl border border-success/20 bg-success-soft px-4 py-2.5">
            <div className="flex items-center gap-1.5 text-xs text-success font-medium">
              <Trophy size={13} />
              <span>Conversion rate</span>
            </div>
            <span className="text-sm font-bold text-success tabular-nums">{funnel.conversionRate}%</span>
          </div>
        </div>
      )}
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function FacebookCampaignDetail() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const nav = useNavigate();
  const [campaign, setCampaign] = useState<FbCampaign | null>(null);
  const [account, setAccount] = useState<MetaAdAccount | null>(null);
  const [adSets, setAdSets] = useState<FbAdSet[] | null>(null);
  const [adsByAdSet, setAdsByAdSet] = useState<Record<string, AdWithCreative[]>>({});
  const [adsLoading, setAdsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [adSetBusy, setAdSetBusy] = useState<Set<string>>(new Set());
  const [adBusy, setAdBusy] = useState<Set<string>>(new Set());
  const [editOpen, setEditOpen] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmAdSetArchive, setConfirmAdSetArchive] = useState<FbAdSet | null>(null);
  const [confirmAdArchive, setConfirmAdArchive] = useState<FbAd | null>(null);
  const [editName, setEditName] = useState('');
  const [editBudgetMajor, setEditBudgetMajor] = useState('');

  const fetchAdsForSet = async (adSetId: string): Promise<AdWithCreative[]> => {
    const r = await facebookAdsService.byAdSet(adSetId);
    return Promise.all(
      r.data.map(async (ad) => {
        if (!ad.creative?.id) return ad;
        try {
          const c = await facebookAdCreativesService.get(ad.creative.id);
          return { ...ad, creative_data: c };
        } catch {
          return ad;
        }
      }),
    );
  };

  const load = async (force = false) => {
    setRefreshing(true);
    setAdsLoading(true);
    setError(null);
    try {
      setCampaign(await facebookCampaignsService.get(campaignId!, force));
      const r = await facebookAdSetsService.byCampaign(campaignId!, force);
      setAdSets(r.data);
      // Eagerly fetch ads for every adset, in parallel.
      const entries = await Promise.all(
        r.data.map(async (s) => [s.id, await fetchAdsForSet(s.id)] as const),
      );
      setAdsByAdSet(Object.fromEntries(entries));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load');
    } finally {
      setRefreshing(false);
      setAdsLoading(false);
    }
  };

  const refreshAdsForSet = async (adSetId: string) => {
    try {
      const list = await fetchAdsForSet(adSetId);
      setAdsByAdSet((m) => ({ ...m, [adSetId]: list }));
    } catch {
      /* toast shown */
    }
  };

  const refreshAdSets = async () => {
    if (!campaignId) return;
    try {
      const r = await facebookAdSetsService.byCampaign(campaignId, true);
      setAdSets(r.data);
    } catch {
      /* toast shown */
    }
  };

  const onAdSetToggle = async (s: FbAdSet) => {
    const isActive = (s.effective_status ?? s.status) === 'ACTIVE';
    setAdSetBusy((set) => new Set(set).add(s.id));
    try {
      if (isActive) await facebookAdSetsService.pause(s.id);
      else await facebookAdSetsService.resume(s.id);
      toast.success(isActive ? 'Ad set paused' : 'Ad set resumed');
      await refreshAdSets();
    } catch {
      /* toast shown */
    } finally {
      setAdSetBusy((set) => {
        const n = new Set(set);
        n.delete(s.id);
        return n;
      });
    }
  };

  const doAdSetArchive = async (s: FbAdSet) => {
    setAdSetBusy((set) => new Set(set).add(s.id));
    try {
      await facebookAdSetsService.archive(s.id);
      toast.success('Ad set archived');
      await refreshAdSets();
    } catch {
      /* toast shown */
    } finally {
      setAdSetBusy((set) => {
        const n = new Set(set);
        n.delete(s.id);
        return n;
      });
    }
  };

  const onAdToggle = async (a: FbAd) => {
    const isActive = (a.effective_status ?? a.status) === 'ACTIVE';
    setAdBusy((set) => new Set(set).add(a.id));
    try {
      if (isActive) await facebookAdsService.pause(a.id, a.adset_id);
      else await facebookAdsService.resume(a.id, a.adset_id);
      toast.success(isActive ? 'Ad paused' : 'Ad resumed');
      await refreshAdsForSet(a.adset_id);
    } catch {
      /* toast shown */
    } finally {
      setAdBusy((set) => {
        const n = new Set(set);
        n.delete(a.id);
        return n;
      });
    }
  };

  const doAdArchive = async (a: FbAd) => {
    setAdBusy((set) => new Set(set).add(a.id));
    try {
      await facebookAdsService.archive(a.id, a.adset_id);
      toast.success('Ad archived');
      await refreshAdsForSet(a.adset_id);
    } catch {
      /* toast shown */
    } finally {
      setAdBusy((set) => {
        const n = new Set(set);
        n.delete(a.id);
        return n;
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    facebookAdAccountService.get().then(setAccount).catch(() => undefined);
    load(false);
  }, [campaignId]);

  const doPause = async () => {
    setActionBusy(true);
    try {
      await facebookCampaignsService.pause(campaignId!);
      toast.success('Paused');
      await load(true);
    } catch {
      /* toast shown */
    } finally {
      setActionBusy(false);
    }
  };

  const doResume = async () => {
    setActionBusy(true);
    try {
      await facebookCampaignsService.resume(campaignId!);
      toast.success('Resumed');
      await load(true);
    } catch {
      /* toast shown */
    } finally {
      setActionBusy(false);
    }
  };

  const doArchive = async () => {
    setActionBusy(true);
    try {
      await facebookCampaignsService.archive(campaignId!);
      toast.success('Archived');
      setConfirmArchive(false);
      await load(true);
    } catch {
      /* toast shown */
    } finally {
      setActionBusy(false);
    }
  };

  const openEdit = () => {
    setEditName(campaign?.name ?? '');
    const major = campaign?.daily_budget
      ? (parseInt(campaign.daily_budget, 10) / 100).toString()
      : '';
    setEditBudgetMajor(major);
    setEditOpen(true);
  };

  const submitEdit = async () => {
    setActionBusy(true);
    try {
      const payload: any = { name: editName };
      if (editBudgetMajor && !isNaN(parseFloat(editBudgetMajor))) {
        payload.daily_budget = String(Math.round(parseFloat(editBudgetMajor) * 100));
      }
      await facebookCampaignsService.update(campaignId!, payload);
      toast.success('Updated');
      setEditOpen(false);
      await load(true);
    } catch {
      /* toast shown */
    } finally {
      setActionBusy(false);
    }
  };

  // Meta reports the account currency; until it does, show '—' rather than guess.
  const currency = account?.currency ?? '';
  const insights = campaign ? getInsights(campaign) : null;

  const effectiveStatus = campaign ? (campaign.effective_status ?? campaign.status) : '';
  const isActive = effectiveStatus === 'ACTIVE';
  const isPaused = effectiveStatus === 'PAUSED';

  if (error) {
    return (
      <>
        <Topbar title="Campaign" breadcrumb="Facebook Ads / Campaigns" />
        <div className="m-6 flex items-start gap-3 rounded-xl border border-danger/30 bg-danger-soft p-4">
          <AlertCircle size={16} className="mt-0.5 shrink-0 text-danger" />
          <p className="text-sm text-danger">{error}</p>
        </div>
      </>
    );
  }

  if (!campaign) {
    return (
      <>
        <Topbar title="Campaign" breadcrumb="Facebook Ads / Campaigns" />
        <div className="px-7 py-7 w-full space-y-4">
          <div className="skeleton h-28 rounded-2xl" />
          <div className="grid grid-cols-4 gap-3">
            {[...Array(8)].map((_, i) => <div key={i} className="skeleton h-28 rounded-2xl" />)}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={campaign.name}
        breadcrumb={`Facebook Ads / Campaigns / ${campaign.name}`}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <CampaignStatusPill status={effectiveStatus} />
            {isPaused && (
              <Button variant="primary" size="sm" loading={actionBusy} onClick={doResume}>
                Resume
              </Button>
            )}
            {isActive && (
              <Button variant="secondary" size="sm" disabled={actionBusy} onClick={doPause}>
                Pause
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={openEdit}>
              Edit
            </Button>
            {campaign.status !== 'ARCHIVED' && (
              <Button variant="danger" size="sm" disabled={actionBusy} onClick={() => setConfirmArchive(true)}>
                Archive
              </Button>
            )}
            <button
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-text-secondary hover:bg-surface-alt hover:text-text-primary disabled:opacity-50 transition-colors"
              onClick={() => load(true)}
              disabled={refreshing}
              aria-label="Refresh"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            </button>
            <a
              href={`https://business.facebook.com/adsmanager/manage/campaigns?act=${(account?.adAccountId ?? '').replace('act_', '')}&selected_campaign_ids=${campaign.id}`}
              target="_blank"
              rel="noreferrer"
            >
              <Button variant="ghost" size="sm" iconRight={<ExternalLink size={12} />}>
                Ads Manager
              </Button>
            </a>
            <Link to="/facebook/campaigns">
              <Button variant="secondary" size="sm">
                Back
              </Button>
            </Link>
          </div>
        }
      />

      <div className="px-7 py-7 w-full space-y-5">
        {/* Cross-platform banner — Meta has no notion of an "Instagram-only"
            campaign at the campaign level. Platforms are decided per ad set
            via targeting.publisher_platforms. When a campaign's ad sets touch
            BOTH FB and IG, the campaign shows up in both modules — this
            banner exists so an operator who reaches this page from one
            module realises edits will affect the other placement too. */}
        {(campaign.platforms ?? []).includes('facebook') &&
         (campaign.platforms ?? []).includes('instagram') && (
          <div className="flex items-start gap-3 rounded-xl border border-info/30 bg-info-soft p-4">
            <Layers size={16} className="mt-0.5 shrink-0 text-info" />
            <div className="text-sm">
              <div className="font-semibold text-info">Cross-platform campaign</div>
              <div className="mt-0.5 text-text-secondary">
                This campaign's ad sets target both Facebook and Instagram. Edits made here apply to both placements,
                and this campaign appears in both the Facebook and Instagram modules.
              </div>
            </div>
          </div>
        )}

        {/* KPI grid */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile label="Spend (30d)" value={formatMetaInsightsValue(insights?.spend, currency)} accent="gold" icon={<BarChart2 size={16} />} />
          <StatTile label="Impressions" value={formatMetaCount(insights?.impressions)} accent="blue" />
          <StatTile label="Clicks" value={formatMetaCount(insights?.clicks)} accent="green" />
          <StatTile label="Reach" value={formatMetaCount(insights?.reach)} accent="amber" />
          <StatTile label="CTR" value={formatMetaPercent(insights?.ctr)} accent="green" />
          <StatTile label="CPC" value={formatMetaInsightsValue(insights?.cpc, currency)} accent="gold" />
          <StatTile label="CPM" value={formatMetaInsightsValue(insights?.cpm, currency)} accent="blue" />
          <StatTile label="Frequency" value={String(insights?.frequency ?? '—')} accent="amber" />
        </div>

        {/* Historical trend (from daily snapshots) */}
        <InsightsTrendStrip campaignId={campaign.id} days={30} currency={currency} />

        {/* Configuration */}
        <Card>
          <SectionHeader title="Configuration" icon={<Settings2 size={16} />} />
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div><span className="text-text-muted">Objective</span><div className="mt-0.5 font-semibold text-text-primary">{campaign.objective}</div></div>
            <div><span className="text-text-muted">Status</span><div className="mt-0.5"><CampaignStatusPill status={campaign.status} /></div></div>
            <div><span className="text-text-muted">Daily budget</span><div className="mt-0.5 tabular-nums font-semibold text-text-primary">{campaign.daily_budget ? formatMetaCurrency(campaign.daily_budget, currency) : '—'}</div></div>
            <div><span className="text-text-muted">Lifetime budget</span><div className="mt-0.5 tabular-nums font-semibold text-text-primary">{campaign.lifetime_budget ? formatMetaCurrency(campaign.lifetime_budget, currency) : '—'}</div></div>
            <div><span className="text-text-muted">Budget remaining</span><div className="mt-0.5 tabular-nums font-semibold text-text-primary">{campaign.budget_remaining ? formatMetaCurrency(campaign.budget_remaining, currency) : '—'}</div></div>
            <div><span className="text-text-muted">Started</span><div className="mt-0.5 font-semibold text-text-primary">{campaign.start_time ? new Date(campaign.start_time).toLocaleString() : '—'}</div></div>
            <div><span className="text-text-muted">Stops</span><div className="mt-0.5 font-semibold text-text-primary">{campaign.stop_time ? new Date(campaign.stop_time).toLocaleString() : 'No end date'}</div></div>
            <div><span className="text-text-muted">Created</span><div className="mt-0.5 font-semibold text-text-primary">{campaign.created_time ? new Date(campaign.created_time).toLocaleDateString() : '—'}</div></div>
          </div>
        </Card>

        {/* Actions breakdown */}
        {insights?.actions && insights.actions.length > 0 && (
          <Card>
            <SectionHeader title="Actions breakdown (30d)" icon={<Zap size={16} />} />
            <div className="grid grid-cols-3 gap-3">
              {insights.actions.map((a) => (
                <div key={a.action_type} className="rounded-xl border border-border bg-surface-alt p-3">
                  <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">
                    {a.action_type.replace(/_/g, ' ')}
                  </div>
                  <div className="mt-2 font-display text-2xl font-bold tabular-nums text-text-primary">
                    {formatMetaCount(a.value)}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Leads attribution */}
        {campaignId && <LeadsAttributionCard campaignId={campaignId} />}

        {/* Ad sets — hierarchical card stack */}
        <Card padded={false} className="overflow-hidden">
          <SectionHeader
            title="Ad Sets"
            subtitle={adSets ? `${adSets.length} ad set${adSets.length === 1 ? '' : 's'}` : undefined}
            icon={<Layers size={16} />}
            action={
              <Button
                size="sm"
                iconLeft={<Plus size={13} />}
                onClick={() => nav(`/facebook/campaigns/${campaign.id}/ad-sets/new`)}
              >
                New Ad Set
              </Button>
            }
            className="px-5 pt-5 pb-3"
          />

          {!adSets ? (
            <div className="space-y-3 p-5 pt-0">
              {[...Array(2)].map((_, i) => <div key={i} className="skeleton h-32 rounded-xl" />)}
            </div>
          ) : adSets.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-10 pt-2 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-soft text-primary">
                <Layers size={20} />
              </div>
              <div className="text-sm font-semibold text-text-primary">No ad sets yet</div>
              <div className="text-xs text-text-muted">Click + New Ad Set to define your audience and budget.</div>
            </div>
          ) : (
            <div className="space-y-3 px-5 pb-5">
              {adSets.map((s) => (
                <AdsetSection
                  key={s.id}
                  adSet={s}
                  ads={adsByAdSet[s.id]}
                  adsLoading={adsLoading}
                  currency={currency}
                  isLoading={adSetBusy.has(s.id)}
                  adLoadingIds={adBusy}
                  onToggleStatus={() => onAdSetToggle(s)}
                  onArchive={() => setConfirmAdSetArchive(s)}
                  onAdToggleStatus={onAdToggle}
                  onAdArchive={(a) => setConfirmAdArchive(a)}
                />
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Edit modal */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Edit campaign">
        <FormField label="Name" required>
          <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
        </FormField>
        <FormField label={`Daily budget (${account?.currency || '—'})`} hint="Leave blank to keep current">
          <Input
            type="number"
            min="1"
            step="0.01"
            value={editBudgetMajor}
            onChange={(e) => setEditBudgetMajor(e.target.value)}
            placeholder="50.00"
          />
        </FormField>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="secondary" onClick={() => setEditOpen(false)}>
            Cancel
          </Button>
          <Button loading={actionBusy} disabled={!editName} onClick={submitEdit}>
            Save
          </Button>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmArchive}
        title="Archive campaign?"
        message={`Archive "${campaign.name}"? This stops the campaign permanently. You can still view its insights, but you can't reactivate it.`}
        danger
        onConfirm={doArchive}
        onCancel={() => setConfirmArchive(false)}
      />

      <ConfirmDialog
        open={!!confirmAdSetArchive}
        title="Archive ad set?"
        message={`Archive "${confirmAdSetArchive?.name ?? ''}"? This stops the ad set permanently.`}
        danger
        onCancel={() => setConfirmAdSetArchive(null)}
        onConfirm={async () => {
          const t = confirmAdSetArchive;
          setConfirmAdSetArchive(null);
          if (t) await doAdSetArchive(t);
        }}
      />

      <ConfirmDialog
        open={!!confirmAdArchive}
        title="Archive ad?"
        message={`Archive "${confirmAdArchive?.name ?? ''}"? This stops the ad permanently.`}
        danger
        onCancel={() => setConfirmAdArchive(null)}
        onConfirm={async () => {
          const t = confirmAdArchive;
          setConfirmAdArchive(null);
          if (t) await doAdArchive(t);
        }}
      />
    </>
  );
}
